import { SortDirection } from '@bp/planung-graphql-types';
import { getCacheTag } from '../hooks/useMemorizedCacheTag';
import {
  _ClassesDocument,
  _ClassesQuery,
  _ClassesQueryVariables,
  _DivisionsDocument,
  _DivisionsQuery,
  _DivisionsQueryVariables,
  _GradeGroupsDocument,
  _GradeGroupsQuery,
  _GradeGroupsQueryVariables,
  _GroupsDocument,
  _GroupsQuery,
  _GroupsQueryVariables,
  _PeopleDocument,
  _PeopleQuery,
  _PeopleQueryVariables,
  _RoomsDocument,
  _RoomsQuery,
  _RoomsQueryVariables,
  _SubjectContainersDocument,
  _SubjectContainersQuery,
  _SubjectContainersQueryVariables,
  _SubjectsDocument,
  _SubjectsQuery,
  _SubjectsQueryVariables,
} from '../types/planung-graphql-client-defs';
import { urqlClient } from './urqlClient';

export const loadBasicData = async ({ schoolyearUuid }: { schoolyearUuid: string }) => {
  const classContext = getCacheTag('CLASSES_FIXED');
  const teacherContext = getCacheTag('PERSONS');
  const roomContext = getCacheTag('ROOMS');
  const subjectContext = getCacheTag('SUBJECTS');
  const subjectContainerContext = getCacheTag('SUBJECT_CONTAINER');
  const gradeGroupContext = getCacheTag('GRADE_GROUPS');
  const groupContext = getCacheTag('GROUPS');
  const divisionContext = getCacheTag('DIVISIONS');

  const [
    teacherData,
    _roomsData,
    subjectData,
    subjectContainerData,
    gradeGroupData,
    classesData,
    groupsData,
    divisionsData,
  ] = await Promise.all([
    urqlClient
      .query<_PeopleQuery, _PeopleQueryVariables>(
        _PeopleDocument,
        {
          options: {
            sort: [{ active: SortDirection.Desc }, { lastName: SortDirection.Asc }, { firstName: SortDirection.Asc }],
          },
        },
        teacherContext,
      )
      .toPromise(),
    urqlClient
      .query<_RoomsQuery, _RoomsQueryVariables>(
        _RoomsDocument,
        {
          options: {
            sort: [
              {
                name: SortDirection.Asc,
              },
              {
                classroom: SortDirection.Asc,
              },
            ],
          },
        },
        roomContext,
      )
      .toPromise(),
    urqlClient
      .query<_SubjectsQuery, _SubjectsQueryVariables>(
        _SubjectsDocument,
        {
          options: {
            sort: [
              {
                active: SortDirection.Desc,
              },
              {
                name: SortDirection.Asc,
              },
            ],
          },
        },
        subjectContext,
      )
      .toPromise(),
    urqlClient
      .query<_SubjectContainersQuery, _SubjectContainersQueryVariables>(
        _SubjectContainersDocument,
        {},
        subjectContainerContext,
      )
      .toPromise(),
    urqlClient
      .query<_GradeGroupsQuery, _GradeGroupsQueryVariables>(_GradeGroupsDocument, {}, gradeGroupContext)
      .toPromise(),
    urqlClient
      .query<_ClassesQuery, _ClassesQueryVariables>(
        _ClassesDocument,
        {
          where: {
            schoolYear: {
              uuid: schoolyearUuid,
            },
          },
          options: {
            sort: [
              {
                sortName: SortDirection.Asc,
              },
            ],
          },
        },
        classContext,
      )
      .toPromise(),
    urqlClient
      .query<_GroupsQuery, _GroupsQueryVariables>(
        _GroupsDocument,
        {
          where: {
            divisionAggregate: {
              count_GT: 0,
            },
            division: {
              class: {
                schoolYear: {
                  uuid: schoolyearUuid,
                },
              },
            },
          },
          options: {
            sort: [
              {
                name: SortDirection.Asc,
              },
            ],
          },
        },
        groupContext,
      )
      .toPromise(),
    urqlClient.query<_DivisionsQuery, _DivisionsQueryVariables>(_DivisionsDocument, {}, divisionContext).toPromise(),
  ]);

  const sortedRooms = _roomsData.data?.rooms.sort((a, b) => {
    const numA = parseInt(a.name.match(/^\d+/)?.[0] || 'Infinity', 10);
    const numB = parseInt(b.name.match(/^\d+/)?.[0] || 'Infinity', 10);
    return numA - numB || a.name.localeCompare(b.name);
  });

  const roomsData: _RoomsQuery = {
    __typename: 'Query',
    rooms: sortedRooms ?? [],
  };

  return {
    teacherData,
    roomsData,
    subjectData,
    subjectContainerData,
    gradeGroupData,
    classesData,
    groupsData,
    divisionsData,
  };
};
